import { useEffect, useRef } from "react";
import "./DotField.css";

// ====================================================================
// DotField — a grid of points in the logo blues (#123976 → #E2EFF7).
// A slow diagonal swell moves through it; the pointer acts as a lens
// that pushes dots aside and lights them, and a click sends a ring out.
// Reduced motion renders one still frame.
// ====================================================================

const DEEP = [18, 57, 118];
const LIGHT = [226, 239, 247];

export function DotField({ spacing = 28, className = "" }) {
	const canvasRef = useRef(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return undefined;
		const ctx = canvas.getContext("2d");
		const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const host = canvas.closest("section") || canvas.parentElement;

		let width = 0;
		let height = 0;
		let raf = 0;
		let visible = true;
		const start = performance.now();
		const pointer = { x: -9999, y: -9999, tx: -9999, ty: -9999, power: 0, target: 0 };
		const rings = [];

		const resize = () => {
			const rect = canvas.getBoundingClientRect();
			const dpr = Math.min(window.devicePixelRatio || 1, 2);
			width = rect.width;
			height = rect.height;
			canvas.width = Math.round(width * dpr);
			canvas.height = Math.round(height * dpr);
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		};

		const draw = (now) => {
			const t = reduced ? 0 : (now - start) / 1000;
			pointer.x += (pointer.tx - pointer.x) * 0.12;
			pointer.y += (pointer.ty - pointer.y) * 0.12;
			pointer.power += (pointer.target - pointer.power) * 0.06;

			for (let i = rings.length - 1; i >= 0; i--) {
				rings[i].r += 7;
				rings[i].life *= 0.965;
				if (rings[i].life < 0.02) rings.splice(i, 1);
			}

			ctx.clearRect(0, 0, width, height);
			const gap = width < 640 ? spacing * 0.85 : spacing;
			const lens = 170;

			for (let y = gap / 2; y < height; y += gap) {
				for (let x = gap / 2; x < width; x += gap) {
					// Ambient swell: a soft diagonal wave
					let glow = 0.5 + 0.5 * Math.sin(x * 0.006 + y * 0.004 - t * 0.6);
					glow *= 0.35;
					let px = x;
					let py = y;

					if (pointer.power > 0.01) {
						const dx = x - pointer.x;
						const dy = y - pointer.y;
						const d = Math.sqrt(dx * dx + dy * dy);
						if (d < lens) {
							const f = (1 - d / lens) * pointer.power;
							const push = f * f * 18;
							px += (dx / (d || 1)) * push;
							py += (dy / (d || 1)) * push;
							glow += f * 1.1;
						}
					}

					for (let r = 0; r < rings.length; r++) {
						const ring = rings[r];
						const dx = x - ring.x;
						const dy = y - ring.y;
						const d = Math.sqrt(dx * dx + dy * dy);
						const band = Math.exp(-((d - ring.r) * (d - ring.r)) / 800);
						glow += band * ring.life * 1.2;
						const lift = band * ring.life * 6;
						px += (dx / (d || 1)) * lift;
						py += (dy / (d || 1)) * lift;
					}

					const k = Math.min(1, glow);
					const c0 = Math.round(DEEP[0] + (LIGHT[0] - DEEP[0]) * k);
					const c1 = Math.round(DEEP[1] + (LIGHT[1] - DEEP[1]) * k);
					const c2 = Math.round(DEEP[2] + (LIGHT[2] - DEEP[2]) * k);
					ctx.fillStyle = `rgba(${c0},${c1},${c2},${0.35 + k * 0.65})`;
					const size = 1 + k * 1.6;
					ctx.fillRect(px - size / 2, py - size / 2, size, size);
				}
			}

			if (!reduced && visible) raf = requestAnimationFrame(draw);
		};

		const local = (e) => {
			const rect = canvas.getBoundingClientRect();
			return [e.clientX - rect.left, e.clientY - rect.top];
		};
		const onMove = (e) => {
			const [x, y] = local(e);
			pointer.tx = x;
			pointer.ty = y;
			if (pointer.x < -9000) {
				pointer.x = x;
				pointer.y = y;
			}
			pointer.target = 1;
		};
		const onLeave = () => {
			pointer.target = 0;
		};
		const onDown = (e) => {
			const [x, y] = local(e);
			rings.push({ x, y, r: 0, life: 1 });
			if (rings.length > 5) rings.shift();
		};

		resize();
		const ro = new ResizeObserver(() => {
			resize();
			if (reduced) draw(performance.now());
		});
		ro.observe(canvas);

		const io = new IntersectionObserver(([entry]) => {
			const was = visible;
			visible = entry.isIntersecting;
			if (visible && !was && !reduced) {
				cancelAnimationFrame(raf);
				raf = requestAnimationFrame(draw);
			}
		});
		io.observe(canvas);

		if (host && !reduced) {
			host.addEventListener("pointermove", onMove);
			host.addEventListener("pointerleave", onLeave);
			host.addEventListener("pointerdown", onDown);
		}
		raf = requestAnimationFrame(draw);

		return () => {
			cancelAnimationFrame(raf);
			ro.disconnect();
			io.disconnect();
			if (host) {
				host.removeEventListener("pointermove", onMove);
				host.removeEventListener("pointerleave", onLeave);
				host.removeEventListener("pointerdown", onDown);
			}
		};
	}, [spacing]);

	return <canvas ref={canvasRef} className={`dotField ${className}`.trim()} aria-hidden="true" />;
}
